import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { BarChart3, ChevronDown, Globe, Mail, Phone, Plus, Send, Sparkles, Upload, User } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from 'sonner';
import { client } from '@/lib/api';
import { formatCents } from '@/lib/format';
import { quoteDisplayTotal } from '@/lib/quote-totals';
import { ITEM_STATUS_LABELS, type ItemStatus } from '@everplan/shared';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import type { ContractWithDetails, QuoteWithDetails } from '@everplan/shared';
import ContractSection from '@/components/ContractSection';
import SendInquiryDialog from '@/components/SendInquiryDialog';
import { authClient } from '@/lib/auth-client';
import UploadQuoteDialog from '@/components/UploadQuoteDialog';
import QuoteCard from '@/components/QuoteCard';
import InlineEdit from '@/components/InlineEdit';
import StageSection from '@/components/StageSection';
import ProgressRail from '@/components/ProgressRail';
import InquirySection from '@/components/InquirySection';

const STATUS_STYLES: Record<ItemStatus, string> = {
  not_started: 'bg-[var(--color-surface-container-high)] text-foreground',
  inquired: 'bg-[var(--color-secondary-container)] text-foreground',
  quoted: 'bg-[var(--color-primary-container)] text-foreground',
  contract_requested: 'bg-[var(--color-tertiary-container)] text-foreground',
  booked: 'bg-primary text-primary-foreground',
};

const ALL_STATUSES: ItemStatus[] = ['not_started', 'inquired', 'quoted', 'contract_requested', 'booked'];

function StatusSelect({ status, onChange, disabled }: { status: ItemStatus; onChange: (s: ItemStatus) => void; disabled?: boolean }) {
  return (
    <div className={`relative inline-flex items-center rounded-full text-xs font-medium ${STATUS_STYLES[status]}`}>
      <select
        value={status}
        onChange={(e) => onChange(e.target.value as ItemStatus)}
        disabled={disabled}
        className="appearance-none bg-transparent pl-2.5 pr-7 py-0.5 cursor-pointer focus:outline-none disabled:opacity-60"
      >
        {ALL_STATUSES.map((s) => (
          <option key={s} value={s}>{ITEM_STATUS_LABELS[s]}</option>
        ))}
      </select>
      <ChevronDown className="pointer-events-none absolute right-2 size-3 opacity-60" />
    </div>
  );
}

const formatContractStatus = (status: string) =>
  status.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

interface ItemDetailContentProps {
  itemId: string;
  projectId: string;
}

export default function ItemDetailContent({ itemId, projectId }: ItemDetailContentProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const expandedQuoteId = searchParams.get('quote');

  const { data: session } = authClient.useSession();
  const [showSendInquiry, setShowSendInquiry] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);

  const [stageOpen, setStageOpen] = useState<{ inquiry: boolean; quotes: boolean; contract: boolean }>({
    inquiry: true,
    quotes: false,
    contract: false,
  });

  const updateStatus = useMutation({
    mutationFn: async (status: ItemStatus) => {
      const res = await client.api.items[':id'].$put({ param: { id: itemId }, json: { status } });
      if (!res.ok) throw new Error('Failed to update status');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['item', itemId] });
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
    },
  });

  const updateItem = useMutation({
    mutationFn: async (patch: Partial<{ name: string; vendorContactName: string | null; vendorEmail: string | null; vendorPhone: string | null; vendorWebsite: string | null; notes: string | null }>) => {
      const res = await client.api.items[':id'].$put({ param: { id: itemId }, json: patch });
      if (!res.ok) throw new Error('Failed to update item');
      return res.json();
    },
    onMutate: async (patch) => {
      await queryClient.cancelQueries({ queryKey: ['item', itemId] });
      const previous = queryClient.getQueryData(['item', itemId]);
      queryClient.setQueryData(['item', itemId], (old: typeof item) => old ? { ...old, ...patch } : old);
      return { previous };
    },
    onError: (_err, _patch, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(['item', itemId], ctx.previous);
      toast.error('Failed to save change');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['item', itemId] });
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
    },
  });

  const { data: item, isLoading, isError } = useQuery({
    queryKey: ['item', itemId],
    queryFn: async () => {
      const res = await client.api.items[':id'].$get({ param: { id: itemId } });
      if (!res.ok) throw new Error('Failed to fetch item');
      return res.json();
    },
    enabled: !!itemId,
  });

  const { data: projectData } = useQuery({
    queryKey: ['project', projectId],
    queryFn: async () => {
      const res = await client.api.projects[':id'].$get({ param: { id: projectId } });
      if (!res.ok) throw new Error('Failed to fetch project');
      return res.json();
    },
  });

  const { data: inquiryVendor } = useQuery({
    queryKey: ['vendor', item?.vendorId],
    queryFn: async () => {
      const res = await client.api.vendors[':id'].$get({ param: { id: item!.vendorId! } });
      if (!res.ok) throw new Error('Failed to fetch vendor');
      return res.json();
    },
    enabled: !!item?.vendorId,
  });

  useEffect(() => {
    if (!item) return;
    if (item.status === 'not_started' || item.status === 'inquired') {
      setStageOpen({ inquiry: true, quotes: false, contract: false });
    } else if (item.status === 'quoted') {
      setStageOpen({ inquiry: false, quotes: true, contract: false });
    } else {
      setStageOpen({ inquiry: false, quotes: false, contract: true });
    }
  }, [item?.id]);

  if (isLoading) {
    return <p className="display-tight text-lg text-muted-foreground">Loading item…</p>;
  }

  if (isError || !item) {
    return (
      <Card className="rounded-3xl shadow-ambient border-destructive/20">
        <CardContent className="pt-6">
          <p className="text-sm text-destructive">
            {isError ? 'Failed to load item. Please try again.' : 'Item not found.'}
          </p>
        </CardContent>
      </Card>
    );
  }

  const quotes = (item.quotes ?? []) as QuoteWithDetails[];

  const inquiries = (item.quoteRequests ?? []) as Array<{
    id: string;
    sentAt: string;
    status: 'pending' | 'received' | 'no_response';
    subject: string;
    body: string;
  }>;

  const contracts = (item.contracts ?? []) as ContractWithDetails[];

  const railState = {
    inquired: inquiries.length > 0 || item.status !== 'not_started',
    quoted: quotes.length > 0 || item.status === 'quoted' || item.status === 'contract_requested' || item.status === 'booked',
    contractRequested: contracts.length > 0 || item.status === 'contract_requested' || item.status === 'booked',
    booked: item.status === 'booked' || contracts.some((c) => !!c.signedAt),
  };

  return (
    <>
      <div className="space-y-6">
      {/* Item header */}
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <InlineEdit
            value={item.name}
            onSave={(name) => updateItem.mutate({ name })}
            className="display-tight text-3xl font-semibold"
          />
          <StatusSelect
            status={item.status}
            onChange={(s) => updateStatus.mutate(s)}
            disabled={updateStatus.isPending}
          />
          {item.vendorId && inquiryVendor?.firstViewedAt == null && (
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Link
                    to={`/vendors/${item.vendorId}`}
                    className="inline-flex items-center text-primary hover:text-primary/80"
                    aria-label="Added to your rolodex"
                  >
                    <Sparkles className="size-4" />
                  </Link>
                </TooltipTrigger>
                <TooltipContent side="top">Added to your rolodex</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
          <div className="flex items-center gap-1.5">
            <User className="size-3.5" />
            <InlineEdit
              value={item.vendorContactName ?? ''}
              onSave={(v) => updateItem.mutate({ vendorContactName: v || null })}
              placeholder="Add contact name"
              className="text-sm text-muted-foreground"
            />
          </div>
          <div className="flex items-center gap-1.5">
            <Mail className="size-3.5" />
            <InlineEdit
              value={item.vendorEmail ?? ''}
              onSave={(v) => updateItem.mutate({ vendorEmail: v || null })}
              type="email"
              placeholder="Add email"
              className="text-sm text-muted-foreground"
            />
          </div>
          <div className="flex items-center gap-1.5">
            <Phone className="size-3.5" />
            <InlineEdit
              value={item.vendorPhone ?? ''}
              onSave={(v) => updateItem.mutate({ vendorPhone: v || null })}
              type="tel"
              placeholder="Add phone"
              className="text-sm text-muted-foreground"
            />
          </div>
          <div className="flex items-center gap-1.5">
            <Globe className="size-3.5" />
            <InlineEdit
              value={item.vendorWebsite ?? ''}
              onSave={(v) => updateItem.mutate({ vendorWebsite: v || null })}
              type="text"
              placeholder="Add website"
              className="text-sm text-muted-foreground"
            />
          </div>
        </div>
        <div className="space-y-0.5">
          <p className="label-caps text-xs text-muted-foreground">Notes</p>
          <InlineEdit
            value={item.notes ?? ''}
            onSave={(v) => updateItem.mutate({ notes: v || null })}
            multiline
            placeholder="Add notes"
            className="text-sm text-muted-foreground"
          />
        </div>
      </div>

      {/* Progress rail */}
      <ProgressRail {...railState} />

      {/* Inquiry section */}
      <StageSection
        label="Inquiry"
        expanded={stageOpen.inquiry}
        onToggle={() => setStageOpen((s) => ({ ...s, inquiry: !s.inquiry }))}
        collapsedSummary={
          inquiries.length === 0
            ? 'Not sent yet'
            : (() => {
                const latest = inquiries[0]!;
                const days = Math.floor((Date.now() - new Date(latest.sentAt).getTime()) / 86400000);
                const tail =
                  latest.status === 'received'
                    ? 'responded'
                    : latest.status === 'no_response'
                      ? 'no response'
                      : `awaiting response (${days}d)`;
                return `Sent ${new Date(latest.sentAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} · ${tail}`;
              })()
        }
        headerActions={
          inquiries.length > 0 ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowSendInquiry(true)}
              disabled={!item.vendorEmail}
              title={!item.vendorEmail ? 'Add a vendor email first' : undefined}
            >
              <Send className="mr-1.5 h-4 w-4" />
              Send another
            </Button>
          ) : null
        }
      >
        <InquirySection
          itemId={itemId}
          inquiries={inquiries}
          vendorEmail={item.vendorEmail ?? null}
          onSendInquiry={() => setShowSendInquiry(true)}
          onSendFollowUp={() => setShowSendInquiry(true)}
        />
      </StageSection>

      {/* Quotes section */}
      <StageSection
        label="Quotes"
        expanded={stageOpen.quotes}
        onToggle={() => setStageOpen((s) => ({ ...s, quotes: !s.quotes }))}
        collapsedSummary={
          quotes.length === 0
            ? 'No quotes yet'
            : (() => {
                const totals = quotes.flatMap((q) => {
                  const { total, min, max, isRange } = quoteDisplayTotal(q);
                  return isRange ? [min, max] : [total || min];
                });
                const validTotals = totals.filter((n) => n > 0);
                const countLabel = `${quotes.length} quote${quotes.length === 1 ? '' : 's'}`;
                if (validTotals.length === 0) return countLabel;
                const lo = Math.min(...validTotals);
                const hi = Math.max(...validTotals);
                return lo === hi
                  ? `${countLabel} · ${formatCents(lo)}`
                  : `${countLabel} · ${formatCents(lo)}–${formatCents(hi)}`;
              })()
        }
        headerActions={
          <>
            {quotes.length >= 2 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/projects/${projectId}/items/${itemId}/compare`)}
              >
                <BarChart3 className="mr-1.5 h-4 w-4" />
                Compare
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowUploadDialog(true)}
            >
              <Upload className="mr-1.5 h-4 w-4" />
              Upload Quote
            </Button>
            <Button
              size="sm"
              onClick={() => navigate(`/projects/${projectId}/items/${itemId}/quote-editor`)}
            >
              <Plus className="mr-1.5 h-4 w-4" />
              Add Manually
            </Button>
          </>
        }
      >
        {/* Quote list */}
        {quotes.length > 0 ? (
          <div className="space-y-3">
            {quotes.map((quote) => (
              <QuoteCard
                key={quote.id}
                quote={quote}
                itemId={itemId}
                projectId={projectId}
                isOnlyQuote={quotes.length === 1}
                {...(quotes.length > 1 && {
                  isUrlExpanded: quote.id === expandedQuoteId,
                  onToggleExpand: () =>
                    setSearchParams((prev) => {
                      const next = new URLSearchParams(prev);
                      if (next.get('quote') === quote.id) next.delete('quote');
                      else next.set('quote', quote.id);
                      return next;
                    }),
                })}
              />
            ))}
          </div>
        ) : (
          <Card className="rounded-3xl shadow-ambient">
            <CardContent className="pt-4">
              <p className="text-sm text-muted-foreground italic">No quotes yet. Add one above.</p>
            </CardContent>
          </Card>
        )}
      </StageSection>

      {/* Contract section */}
      <StageSection
        label="Contract"
        expanded={stageOpen.contract}
        onToggle={() => setStageOpen((s) => ({ ...s, contract: !s.contract }))}
        collapsedSummary={
          contracts.length === 0
            ? 'No contract yet'
            : (() => {
                const c = contracts[0]!;
                if (c.signedAt) {
                  return `Signed ${new Date(c.signedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })}`;
                }
                return c.status ? formatContractStatus(c.status) : 'Pending';
              })()
        }
      >
        <ContractSection
          itemId={itemId}
          contracts={contracts}
          selectedQuote={quotes.find((q) => q.isSelected) ?? (quotes.length === 1 ? quotes[0]! : null)}
        />
      </StageSection>
      </div>

      {(() => {
        const primaryContact = item.vendorId
          ? (inquiryVendor?.contacts ?? []).find(
              (c) => c.isPrimary && c.deactivatedAt === null,
            ) ?? null
          : null;
        const resolvedVendorEmail = primaryContact?.email
          ?? inquiryVendor?.email
          ?? item.vendorEmail
          ?? '';
        const resolvedContactName = primaryContact?.name ?? item.vendorContactName ?? null;
        return (
          <SendInquiryDialog
            open={showSendInquiry}
            onOpenChange={setShowSendInquiry}
            itemId={itemId}
            itemName={item.name}
            vendorEmail={resolvedVendorEmail}
            vendorContactName={resolvedContactName}
            plannerName={session?.user?.name ?? ''}
            plannerEmail={session?.user?.email ?? ''}
            project={{
              person1Name: projectData?.person1Name ?? null,
              person2Name: projectData?.person2Name ?? null,
              weddingDate: projectData?.weddingDate ?? null,
              guestCount: projectData?.guestCount ?? null,
              venueName: projectData?.venueName ?? null,
            }}
            vendorId={item.vendorId ?? null}
            vendorContactId={primaryContact?.id ?? null}
          />
        );
      })()}

      <UploadQuoteDialog
        open={showUploadDialog}
        onOpenChange={setShowUploadDialog}
        itemId={itemId}
        projectId={projectId}
      />
    </>
  );
}
